import {useTranslation} from '@aboutyou/i18n';
import {Fade} from '@aboutyou/transitions';
import {
  COLORS,
  Dropdown,
  NumberBadge,
  Text,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import React, {useRef} from 'react';
import styled, {css} from 'styled-components';
import {OnboardingHint} from './OnboardingHint';
import {PersonalizedFilterList} from './PersonalizedFilterList';
import {usePersonalizedFiltersHint} from './utils/usePersonalizedFiltersHint';
import {PersonalizedFilter} from './utils/usePersonalizedFiltersStore';

const badgeStyles = {activeBg: COLORS.Black, activeColor: COLORS.White};

interface YourFiltersButtonProps {
  hintVisibleTimeMs?: number;
  isDropdownOpen: boolean;
  openDropdown: () => void;
  closeDropdown: VoidFunction;
  showSuccessMessage?: boolean;
  personalizedFilters: PersonalizedFilter[];
  deletePersonalizedFilter: (personalizedFilter: PersonalizedFilter) => void;
  appliedPersonalizedFilterId?: string;
}

export const YourFiltersButton: React.FC<YourFiltersButtonProps> = ({
  hintVisibleTimeMs = 8000,
  isDropdownOpen,
  openDropdown,
  closeDropdown,
  showSuccessMessage,
  personalizedFilters,
  deletePersonalizedFilter,
  appliedPersonalizedFilterId,
}) => {
  const {t} = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const {shouldShowHint, closeHint} =
    usePersonalizedFiltersHint(hintVisibleTimeMs);

  const onOpenerClick = () => {
    openDropdown();
    closeHint();
  };

  return (
    <Wrapper data-active={Boolean(appliedPersonalizedFilterId)}>
      <StyledDropdown
        data-testid="yourFiltersButton"
        ref={ref}
        $animate={shouldShowHint}
        openerContent={
          <OpenerContent>
            {t('Your Filters')}
            <StyledNumberBadge
              active
              activeStyles={badgeStyles}
              count={1}
              size="extrasmall"
            />
          </OpenerContent>
        }
        onOpenerClick={onOpenerClick}
        openerType={'caret'}
        onClose={closeDropdown}
        bodyMarginTop={10}
        bodyContent={
          <PersonalizedFilterList
            personalizedFilters={personalizedFilters}
            showSuccessMessage={Boolean(showSuccessMessage)}
            deletePersonalizedFilter={deletePersonalizedFilter}
            closeDropdown={closeDropdown}
            appliedPersonalizedFilterId={appliedPersonalizedFilterId}
          />
        }
        open={isDropdownOpen}
      />
      <Fade mounted={shouldShowHint} duration={400}>
        <OnboardingHintWrapper>
          <OnboardingHint yourFilterButtonRef={ref} onClose={closeHint} />
        </OnboardingHintWrapper>
      </Fade>
    </Wrapper>
  );
};

const OpenerContent = styled(Text)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
`;

const StyledNumberBadge = styled(NumberBadge)`
  display: none;
  margin-left: 0.375rem;
  padding: 0 8px;
`;

const StyledDropdown = styled(Dropdown)<{$animate: boolean}>`
  border: 2px solid ${COLORS.Green};
  border-radius: 3px;

  & > button {
    background-color: ${COLORS.Green};
    border: none;

    svg {
      position: relative;
      left: -0.2rem;
      opacity: 1;
    }

    &:before {
      position: absolute;
      top: 0;
      left: 0;
      content: '';
      border: 2px solid ${COLORS.Green};
      border-radius: 6px;
      width: 100%;
      height: 100%;
      z-index: 2;
      display: none;
    }
  }

  ${({$animate}) =>
    $animate &&
    css`
      animation: pulse 1.4s infinite;

      & > button:before {
        display: block;
        border-radius: unset;
        border-color: ${COLORS.White};
      }

      // eslint-disable-next-line local-rules/css-layout-animation
      @keyframes pulse {
        0% {
          border-color: rgba(112, 234, 179, 1);
        }
        50% {
          border-color: rgba(112, 234, 179, 0.4);
        }
        100% {
          border-color: rgba(112, 234, 179, 1);
        }
      }
    `}
`;

const Wrapper = styled.div`
  position: relative;

  &[data-active='true'] {
    ${StyledDropdown} {
      border: 1px solid ${COLORS.Black};
      > button {
        border-radius: 3px;
      }
    }

    ${StyledNumberBadge} {
      display: flex;
    }
  }
`;

const OnboardingHintWrapper = styled.div`
  position: relative;
  top: 15px;
  z-index: ${Z_INDEXES.PERSONALIZED_FILTERS_HINT};
`;
